import client from './client';

/**
 * Per-service unit cost (AI-8128 phase 1).
 *
 * The figure that matters is `cost_per_million_usd`. Absolute cost conflates
 * "expensive per call" with "called a lot", and those two need opposite
 * responses — a service costing $8k per million invocations is a design
 * problem, one costing $2 per million but running four million times a month
 * is simply doing its job.
 *
 * Money arrives as strings: the server serialises Decimal that way to avoid
 * float drift, and per-invocation costs sit around 1e-7 USD where float
 * rounding is visible.
 */

export type UnitCostPeriod = '1d' | '7d' | '14d' | '30d' | 'custom';

export interface UnitCostWindowParams {
  period?: UnitCostPeriod;
  start?: string; // YYYY-MM-DD
  end?: string; // YYYY-MM-DD, exclusive
  aws_service?: string;
  env?: string;
}

export interface UnitCostWindow {
  period: UnitCostPeriod;
  start: string;
  end: string;
}

export interface ServiceUnitCostRow {
  service_slug: string | null;
  env: string | null;
  account_id: string;
  aws_service: string;
  total_cost_usd: string;
  invocations: number;
  gb_seconds: string;
  resource_count: number;
  /** null when no invocations were recorded — a coverage gap, not a free service. */
  cost_per_invocation_usd: string | null;
  cost_per_million_usd: string | null;
  /** All-in cost of 1000 calls — readable at the volumes these services run at. */
  cost_per_1000_usd: string | null;
  /** Capacity held warm. Billed on the clock, so traffic does not explain it. */
  reserved_cost_usd: string;
  /** Everything that moves with traffic: execution, requests, transfer. */
  usage_cost_usd: string;
  /** Marginal cost of 1000 more calls — usage only, reserved excluded. */
  usage_cost_per_1000_usd: string | null;
  last_seen: string | null;
}

export interface ServiceUnitCostResponse {
  window: UnitCostWindow;
  rows: ServiceUnitCostRow[];
  total_cost_usd: string;
  total_invocations: number;
  blended_cost_per_million_usd: string | null;
  blended_cost_per_1000_usd: string | null;
  /** Window-wide reserved-capacity spend — the part traffic cannot explain. */
  total_reserved_cost_usd: string;
  /** Most recent day in the ledger. Older than yesterday means the sync is behind. */
  data_through: string | null;
  /**
   * Oldest day the ledger actually holds inside the requested window. A 30d
   * request over a ledger that started collecting last week returns 30d as the
   * window but only has 7d of data, and labelling that as "30 days" overstates
   * what the numbers cover.
   */
  data_from: string | null;
}

export interface UsageTypeRow {
  usage_type: string;
  usage_unit: string | null;
  cost_usd: string;
  usage_qty: string;
}

export interface DailyCostPoint {
  period_date: string;
  cost_usd: string;
  invocations: number;
}

export interface ResourceSpecRow {
  resource_key: string;
  resource_id: string;
  env: string | null;
  region: string | null;
  memory_mb: number | null;
  architecture: string | null;
  timeout_s: number | null;
  ephemeral_storage_mb: number | null;
  runtime: string | null;
  package_type: string | null;
  last_modified: string | null;
  memory_utilisation_note: string | null;
  /** AWS resource tags, best-effort: ownership lives here when teams set it. */
  tags: Record<string, string>;
}

/**
 * What the service catalogue knows about a billed service.
 *
 * Joined server-side on a normalised slug, so ownership arrives without
 * anyone having tagged the Lambda. Null when the service is billed but not
 * registered — itself worth showing.
 */
export interface ServiceCatalogRow {
  name: string;
  slug: string;
  description: string | null;
  service_group: string | null;
  lifecycle_stage: string | null;
  owner_team: string | null;
  repo_full_name: string | null;
  repo_url: string | null;
  repo_language: string | null;
  links: Record<string, string>;
}

export interface ServiceUnitCostDetail {
  window: UnitCostWindow;
  service_slug: string;
  env: string | null;
  summary: ServiceUnitCostRow | null;
  usage_types: UsageTypeRow[];
  daily: DailyCostPoint[];
  specs: ResourceSpecRow[];
  catalog: ServiceCatalogRow | null;
}

export interface AwsServiceCoverageRow {
  aws_service: string;
  cost_usd: string;
  resources: number;
}

export interface UnitCostCoverageResponse {
  window: UnitCostWindow;
  total_cost_usd: string;
  no_resource_id_cost_usd: string;
  unmapped_cost_usd: string;
  attributable_cost_usd: string;
  attributable_pct: number;
  by_aws_service: AwsServiceCoverageRow[];
  data_through: string | null;
}

export async function fetchUnitCosts(
  params?: UnitCostWindowParams,
): Promise<ServiceUnitCostResponse> {
  const { data } = await client.get<ServiceUnitCostResponse>('/unit-costs', { params });
  return data;
}

export async function fetchUnitCostCoverage(
  params?: UnitCostWindowParams,
): Promise<UnitCostCoverageResponse> {
  const { data } = await client.get<UnitCostCoverageResponse>('/unit-costs/coverage', { params });
  return data;
}

export async function fetchUnitCostDetail(
  serviceSlug: string,
  params?: UnitCostWindowParams,
): Promise<ServiceUnitCostDetail> {
  const { data } = await client.get<ServiceUnitCostDetail>(
    `/unit-costs/${encodeURIComponent(serviceSlug)}`,
    { params },
  );
  return data;
}
